import { KeyboardEvent, useState } from 'react';
import { Divider, IconButton, InputAdornment } from '@mui/material';
import { BodyMedium, BodySmall, BodySmallLink, Column, TitleLarge } from '../../../styles';
import { LoginCardContainer } from '../styles';
import { FilledButton, PMPSnackbar, DHTextField, TextButton } from '../../../components';
import { useTheme } from '@emotion/react';
import { useTranslations } from 'next-intl';
import { useFormValidationState } from '../../../hooks/useFormValidationState/useFormValidationState';
import { useBreakpoint, useToggle } from '../../../hooks';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { assertUnreachable, isEmail } from '../../../utils';
import { useRouter } from 'next/navigation';
import { ROUTES } from '../../../../constants';
import { LoginError } from '../../../utils/loginUtils';
import { onUserForgotPassword } from '../../../api/users/updateUser';

enum LoginCardContent {
    Login,
    ResetPassword,
    ResetMailSent
}

interface Props {
    error?: LoginError;
    onLogin: (email: string, password: string) => Promise<void>;
}

export const LoginCard = ({ error, onLogin }: Props) => {
    const theme = useTheme();
    const breakpoint = useBreakpoint();
    const t = useTranslations('login');
    const router = useRouter();

    const [loginCardContent, setLoginCardContent] = useState<LoginCardContent>(
        LoginCardContent.Login
    );
    const [email, setEmail] = useFormValidationState<string>('', true, isEmail);
    const [password, setPassword] = useFormValidationState('', true);
    const [showPassword, setShowPassword] = useToggle();
    const [showErrorSnackbar, toggleErrorSnackbar] = useToggle();

    const redirectToRegistration = () => router.push(ROUTES.Signup);

    const login = () => onLogin(email.value, password.value);
    const onResetPassword = () => {
        onUserForgotPassword(email.value)
            .then(() => {
                setLoginCardContent(LoginCardContent.ResetMailSent);
            })
            .catch(() => toggleErrorSnackbar(true));
    };

    const canLogin = email.isValid && password.isValid;

    const handleInputKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            if (loginCardContent === LoginCardContent.ResetPassword && email.isValid)
                onResetPassword();
            else if (canLogin) login();
        }
    };

    const renderLoginError = (error: LoginError) => {
        switch (error) {
            case LoginError.Credentials:
                return (
                    <BodySmall color={theme.colors.error}>
                        {t('login_failed_credentials_error')}
                    </BodySmall>
                );
            case LoginError.Unconfirmed:
                return (
                    <BodySmall color={theme.colors.error}>
                        {
                            'Wir haben Ihnen eine Bestätigungs-Email gesendet. Bitte bestätigen Sie Ihre E-Mail-Adresse, bevor Sie sich einloggen können. '
                        }
                        <BodySmallLink href={ROUTES.ResendConfirmation} withUnderline>
                            {'Bestätigungs-Email erneut senden.'}
                        </BodySmallLink>
                    </BodySmall>
                );
            case LoginError.NotApproved:
                return (
                    <BodySmall color={theme.colors.error}>
                        {
                            'Ihr Account ist noch nicht genehmigt. Bitte warten Sie auf die Genehmigung durch einen Administrator.'
                        }
                    </BodySmall>
                );
            case LoginError.Unknown:
                return (
                    <BodySmall color={theme.colors.error}>
                        {t('login_failed_unknown_error')}
                    </BodySmall>
                );
            default:
                assertUnreachable(error);
        }
    };

    const renderLoginContent = () => {
        if (loginCardContent === LoginCardContent.Login)
            return (
                <Column gap={3} height={'100%'} overflow={'auto'}>
                    <Column>
                        <TitleLarge sx={{ color: theme.colors.onBackground, mt: theme.spacing(2) }}>
                            {t('login')}
                        </TitleLarge>
                    </Column>
                    {error && renderLoginError(error)}
                    <DHTextField
                        label={t('input_email')}
                        placeholder={t('input_email')}
                        value={email.value}
                        isValid={email.isValid}
                        onChange={({ target }) => setEmail(target.value)}
                        onKeyUp={handleInputKeyUp}
                        errorText={t('login_card_input_email_error')}
                    />
                    <DHTextField
                        type={showPassword ? 'text' : 'password'}
                        label={t('input_pw')}
                        placeholder={t('input_pw')}
                        value={password.value}
                        onChange={({ target }) => setPassword(target.value)}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton
                                            onClick={() => setShowPassword()}
                                            sx={{ backgroundColor: 'transparent' }}
                                        >
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                        onKeyUp={handleInputKeyUp}
                    />
                    <FilledButton
                        fullWidth
                        label={t('login')}
                        disabled={!canLogin}
                        onClick={login}
                        sx={{ mx: 0.5 }}
                    />

                    <TextButton
                        label={t('forgot_pw')}
                        onClick={() => setLoginCardContent(LoginCardContent.ResetPassword)}
                        sx={{ width: 'fit-content', alignSelf: 'center' }}
                        labelColor={theme.colors.onSurface}
                    />

                    <Divider sx={{ mx: theme.spacing(2) }} />
                    <Column>
                        <BodyMedium textAlign={'center'}>
                            {t('login_card_no_account_redirect_info')}
                        </BodyMedium>
                        <TextButton
                            label={t('login_card_no_account_redirect_button_label')}
                            onClick={redirectToRegistration}
                            sx={{ m: theme.spacing(1.5) }}
                        />
                    </Column>
                </Column>
            );
        else return null;
    };

    const renderResetPasswordContent = () => {
        if (loginCardContent === LoginCardContent.ResetPassword)
            return (
                <Column gap={3} height={'100%'} overflow={'auto'}>
                    <TitleLarge mt={2} sx={{ color: theme.colors.onBackground }}>
                        {t('forgot_pw')}
                    </TitleLarge>
                    <BodyMedium>{t('forgot_pw_text')}</BodyMedium>
                    <DHTextField
                        label={t('input_email')}
                        placeholder={t('input_email')}
                        value={email.value}
                        isValid={email.isValid}
                        onChange={({ target }) => setEmail(target.value)}
                        onKeyUp={handleInputKeyUp}
                        errorText={t('login_card_input_email_error')}
                    />
                    <FilledButton
                        label={t('reset_pw')}
                        disabled={!email.isValid}
                        onClick={onResetPassword}
                    />

                    <Divider sx={{ mx: theme.spacing(2) }} />
                    <TextButton
                        label={t('back_to_login')}
                        onClick={() => setLoginCardContent(LoginCardContent.Login)}
                        style={{ width: 'fit-content', alignSelf: 'center' }}
                    />
                </Column>
            );
        else return null;
    };

    const renderPasswordResetSent = () => {
        if (loginCardContent === LoginCardContent.ResetMailSent)
            return (
                <Column gap={3} height={'100%'} overflow={'auto'}>
                    <TitleLarge mt={2} sx={{ color: theme.colors.onBackground }}>
                        {t('check_mails')}
                    </TitleLarge>
                    <BodyMedium>{t('check_mails_text', { email: email.value })}</BodyMedium>
                    <TextButton
                        label={t('back_to_login')}
                        onClick={() => setLoginCardContent(LoginCardContent.Login)}
                        style={{ width: 'fit-content', alignSelf: 'center' }}
                    />
                </Column>
            );
        else return null;
    };

    return (
        <>
            <LoginCardContainer elevation={3} breakpoint={breakpoint}>
                {renderLoginContent()}
                {renderResetPasswordContent()}
                {renderPasswordResetSent()}
            </LoginCardContainer>
            {showErrorSnackbar && (
                <PMPSnackbar
                    isOpen={showErrorSnackbar}
                    toggleIsOpen={toggleErrorSnackbar}
                    message={t('pw_reset_error')}
                />
            )}
        </>
    );
};
